import React, { useEffect, useMemo, useState } from 'react';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canUserViewOfficeReports, isOfficeAdminUser } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment, { Moment } from "moment";
import { OfficeConfirmationLineChart } from "./components/OfficeConfirmationLineChart";
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { OfficeDropDownList } from '@shared/ui/organisms/OfficeDropDownList';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import {
  EmailCheckInLine,
  EmailCheckInStatus,
  getCheckInSummary,
  getEmailCheckIns,
  toCsvDownload,
} from '@features/reporting/services/ReportingService';
import { failureNotification } from "@shared/store/notification.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import Colours from '@shared/ui/atoms/Colours';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { PRETTY_FULL_DAY_DATE_FORMAT } from '@shared/utils/DateUtils';
import { useTranslation } from "react-i18next";

function parseReportData(list: any[]) {
  return list.map(item => ({
      toolTipTitle: moment(item.date).format('Do MMMM YYYY'),
      date: item.date,
      officeBookings: item.officeUsage,
      confirmations: item.numberOfCheckIns,
  }));
}

const PERIOD_LABELS: Record<string, string> = { ALL_DAY: 'All day', AM: 'AM', PM: 'PM' };

const STATUSES: Record<EmailCheckInStatus, { label: string, colour: string, explanation: string }> = {
  CONFIRMED: { label: 'Confirmed', colour: Colours.darkGreen, explanation: 'Clicked the link in the check-in email' },
  NOT_CONFIRMED: { label: 'Not confirmed', colour: Colours.orange, explanation: "Was sent the check-in email but hasn't clicked the link" },
  NO_EMAIL: { label: 'No email sent', colour: Colours.darkGrey, explanation: 'No check-in email was sent, usually because they booked after the emails went out' },
};

export function OfficeCheckInReportingPage(props: Props) {
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [selectedDay, setSelectedDay] = useState<Moment>(moment());
  const [reportData, setReportData] = useState<any>();
  const [selectedOffice, setSelectedOffice] = useState<OfficeEntity | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState<EmailCheckInLine[] | undefined>();
  const [loadingPeople, setLoadingPeople] = useState(false);
  const currentUser = useSelector(selectCurrentUser);
  const canViewReport = useMemo(() => canUserViewOfficeReports(currentUser), [currentUser]);
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const dayKey = selectedDay.format('YYYY-MM-DD');
  const isFutureDay = selectedDay.isAfter(moment(), 'day');
  const canRunForOffice = !!selectedOffice && !(isOfficeAdminUser(currentUser) && currentUser?.defaultLocationId !== selectedOffice.id);

  useEffect(() => {
    if (canViewReport && selectedOffice) {
      if (isOfficeAdminUser(currentUser) && currentUser?.defaultLocationId !== selectedOffice.id) {
        dispatch(failureNotification(`You can only run office report on your own office`));
        return;
      }

      setLoading(true)
      getCheckInSummary(selectedOffice, selectedDate)
        .then(summary => setReportData(parseReportData(summary)))
        .catch(() => dispatch(failureNotification("Failed to get report data")))
        .finally(() => setLoading(false));
    }
  }, [canViewReport, selectedDate, selectedOffice, dispatch, currentUser]);

  useEffect(() => {
    if (!canViewReport || !selectedOffice || !canRunForOffice || isFutureDay) {
      setPeople(undefined);
      setLoadingPeople(false);
      return;
    }
    let cancelled = false;
    setLoadingPeople(true);
    getEmailCheckIns(selectedOffice, moment(dayKey))
      .then(lines => { if (!cancelled) setPeople(lines); })
      .catch(() => {
        if (cancelled) return;
        setPeople(undefined);
        dispatch(failureNotification("Failed to get email check-ins"));
      })
      .finally(() => { if (!cancelled) setLoadingPeople(false); });
    return () => { cancelled = true; };
  }, [canViewReport, selectedOffice, canRunForOffice, dayKey, isFutureDay, dispatch]);

  const changeMonth = (date: Moment) => {
    setSelectedDate(date);
    // Today's list in the current month, otherwise the first of the month
    setSelectedDay(date.isSame(moment(), 'month') ? moment() : date.clone().startOf('month'));
  }

  const changeDay = (day: Moment) => {
    setSelectedDay(day);
    if (!day.isSame(selectedDate, 'month')) {
      setSelectedDate(day.clone());
    }
  }

  const counts = useMemo(() => ({
    booked: (people ?? []).filter(line => line.period).length,
    confirmed: (people ?? []).filter(line => line.status === 'CONFIRMED').length,
    notConfirmed: (people ?? []).filter(line => line.status === 'NOT_CONFIRMED').length,
    noEmail: (people ?? []).filter(line => line.status === 'NO_EMAIL').length,
  }), [people]);

  const downloadCsv = () => {
    if (!people || people.length === 0 || !selectedOffice) return;
    const rows = people.map(line => ({
      'Date': selectedDay.format('DD/MM/YYYY'),
      'Name': line.name,
      'Team': line.teamName ?? '',
      'Booked': line.period ? PERIOD_LABELS[line.period] : 'Not booked',
      'Office': line.officeLabel ?? '',
      'Desk': line.deskLabel ?? '',
      'Check-in': STATUSES[line.status].label,
    }));
    const officePart = selectedOffice.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    toCsvDownload(rows, `email-check-in-${officePart}-${dayKey}`);
  }

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      {loading && <LoadingSpinner fullScreen={true} />}
      <div>
        {canViewReport ? <>
          <HeadlineLarge style={{marginBottom: 24}}>{t('reports-page.checkin-title')}</HeadlineLarge>
          <SpaceBetweenRow>
            <DateFlicker dateScale={DateScale.Month} updateGlobalDate={false} date={selectedDate} onChange={changeMonth} />

            <div style={{minWidth: 300}}>
              <OfficeDropDownList onSelect={setSelectedOffice} selectDefaultOffice={true} disabled={loading} />
            </div>
          </SpaceBetweenRow>

          {selectedOffice && reportData && <>
            <BodyLarge weight={600} style={{marginTop: 24, marginBottom: 8}}>{t('reports-page.office-usage-confirmation')}</BodyLarge>
            <OfficeConfirmationLineChart reportData={reportData}
                                         selectedDate={dayKey}
                                         onSelectDate={date => changeDay(moment(date))} />
          </>}

          {selectedOffice && canRunForOffice && <>
            <BodyLarge weight={600} style={{marginTop: 40, marginBottom: 4}}>Who checked in</BodyLarge>
            <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 16}}>
              Everyone booked into {selectedOffice.label} on the day, and whether they clicked the link in the check-in email to confirm. Click a day on the chart to see it here.
            </BodyRegular>

            <DayRow>
              <DateFlicker dateScale={DateScale.Day}
                           updateGlobalDate={false}
                           date={selectedDay}
                           onChange={changeDay}
                           dateFormat={PRETTY_FULL_DAY_DATE_FORMAT}
                           disabled={loadingPeople} />
              {!loadingPeople && people && people.length > 0 && <OutlineButton text="button.download-csv" click={downloadCsv} />}
            </DayRow>

            {isFutureDay && (
              <BodyRegular colour={Colours.darkGrey} style={{marginTop: 16}}>This day hasn't happened yet.</BodyRegular>
            )}

            {loadingPeople && (
              <Center>
                <LoadingSpinner hideBorder={true} />
              </Center>
            )}

            {!loadingPeople && people && people.length === 0 && (
              <BodyRegular colour={Colours.darkGrey} style={{marginTop: 16}}>No one was booked into this office on this day.</BodyRegular>
            )}

            {!loadingPeople && people && people.length > 0 && <>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryValue>{counts.booked}</SummaryValue>
                  <SummaryLabel>Booked in</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{counts.confirmed}</SummaryValue>
                  <SummaryLabel>Confirmed</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{counts.notConfirmed}</SummaryValue>
                  <SummaryLabel>Not confirmed</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{counts.noEmail}</SummaryValue>
                  <SummaryLabel>No email sent</SummaryLabel>
                </SummaryItem>
              </SummaryGrid>

              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Team</Th>
                      <Th>Booked</Th>
                      <Th>Office</Th>
                      <Th>Desk</Th>
                      <Th>Check-in</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {people.map(line => (
                      <Row key={line.userId}>
                        <Td style={{fontWeight: 600}}>{line.name}</Td>
                        <Td>{line.teamName || '-'}</Td>
                        <Td>
                          {line.period ? PERIOD_LABELS[line.period] : (
                            <span title="Confirmed from the check-in email but no longer booked into this office">Not booked</span>
                          )}
                        </Td>
                        <Td>{line.officeLabel || '-'}</Td>
                        <Td>{line.deskLabel || '-'}</Td>
                        <Td>
                          <Status style={{color: STATUSES[line.status].colour}} title={STATUSES[line.status].explanation}>
                            {STATUSES[line.status].label}
                          </Status>
                        </Td>
                      </Row>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </>}
          </>}

        </> : <>
          <AccessRequired visible={!canViewReport} teamAdminsRequired={false} companyAdminsRequired={true} />
        </>}
      </div>
    </ReportingContainer>
  )
}

interface Props {
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const DayRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const TableWrapper = styled.div`
  margin-top: 16px;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  white-space: nowrap;
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
  vertical-align: top;
`

const Row = styled.tr`
  &:hover {
    background-color: ${Colours.blue10};
  }
`

const Status = styled.span`
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
`
